import React, { useState, useCallback, useMemo } from "react"
import {
  View,
  TextInput,
  StyleSheet,
  FlatList,
} from "~/ui/primitives"
import { useSafeAreaInsets } from "~/ui/safe-area"
import { debounce } from "lodash"   // 你可以用你项目里的 debounce
import { BetweenFlex, CenterFlex, ColumnFlex, IconFont, Button, Text } from "~components/El"
import styled, { useTheme } from "~/ui/styled"
import { Avatar } from "~widgets/avatar"
import { useStores } from "~models"
import { ChatTypes } from "~proto"

interface ContactSearchProps {
  groupType: ChatTypes,
  members: any[],
  onSelect: (member: any) => void,
}

const ContactRow = styled(BetweenFlex)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding-top: 10px;
  padding-bottom: 10px;
`

const SearchBar = styled(CenterFlex)`
  height: 40px;
  margin-top: 10px;
  margin-bottom: 10px;
  padding: 0 12px;
  background-color: ${({ theme }) => theme.colors.inputBackground};
  border-radius: 20px;
`

const SearchInput = styled(TextInput)`
  flex: 1;
  min-width: 0;
  margin-left: 8px;
  border: none;
  outline: none;
  background: transparent;
`

const ContactSearchScreen: React.FC<ContactSearchProps> = ({ groupType, members, onSelect }) => {
  const theme = useTheme()
  const insets = useSafeAreaInsets()
  const {contactStore} = useStores()
  const [search, setSearch] = useState("")
  // ---- 防抖搜索 ----
  const handleSearch = useCallback(
    debounce((text) => {
      // TODO：触发 API 搜索
    }, 400),
    []
  )

  const onChangeText = (text) => {
    setSearch(text)
    handleSearch(text)
  }

  const filteredData = useMemo(() => {
    return contactStore.getContactListByKeyword(search.trim())
  }, [search])

  const total = useMemo(() => {
    if (groupType === ChatTypes.GROUP_MINE) return 11
    if (groupType === ChatTypes.GROUP_DRAGON) return 4
    return 3
  }, [groupType])

  const renderItem = useCallback(({ item }) => {
    const selected = members.some(member => member.user_id === item.user_id)

    return (
        <BetweenFlex
          width="100%"
          height="auto"
          bg="transparent"
          onClick={() => onSelect(item)}
        >
          <CenterFlex mr="12px" width="40px" height="auto">
            <Avatar
              avatar={item.user?.avatar}
              name={item.user?.name}
              size={40}
            />
          </CenterFlex>
          <ContactRow flex={1} width="100%">
            <ColumnFlex width="100%" height="auto">
              <Text color={theme.colors.text} fontSize={16}  fontWeight={500}>
                {item.user?.name || ""}
              </Text>
              <Text color={theme.colors.secondaryText} fontSize={12}>
                {item.user?.online ? "在线" : "离线"}
              </Text>
            </ColumnFlex>
            <CenterFlex width="40px" height="auto">
              <IconFont
                name={selected ? "cg" : "yuan"}
                size={15}
                color={selected ? theme.colors.primary : theme.colors.secondaryText}
              />
            </CenterFlex>
          </ContactRow>
      </BetweenFlex>
    )
  }, [members])

  return (
    <ColumnFlex height="auto"  p="0 8px" style={styles.container}>
      <BetweenFlex width="100%" height="auto">
        <Text color={theme.colors.text} fontWeight={600} fontSize={16}>选择成员</Text>
        <Text color={theme.colors.secondaryText} fontSize={16}>已选择{members.length} 人</Text>
      </BetweenFlex>
      <SearchBar>
        <IconFont
          name="ss"
          color={theme.colors.inputPlaceholderColor}
          size={12}
        />
        <SearchInput
          autoCapitalize="none"
          placeholder="搜索好友"
          onChangeText={onChangeText}
          placeholderTextColor={theme.colors.inputPlaceholderColor}
        />
      </SearchBar>
      {/* 联系人列表 */}
      <FlatList
        style={{ flex: 1 }}
        data={filteredData}
        keyExtractor={(item) => item.id.toString()}
        renderItem={renderItem}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        contentContainerStyle={{ flexGrow: 1, paddingBottom: insets.bottom + 60 }}
      />

    </ColumnFlex>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    backgroundColor: "#fff",
  },
  searchBox: {
    height: 40,
    backgroundColor: "#f1f2f5",
    borderRadius: 20,
    paddingHorizontal: 12,
    justifyContent: "center",
    marginBottom: 10,
  },
  input: {
    flex: 1,
  },
  item: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
  },
  itemText: {
    fontSize: 16,
  },
})

export default ContactSearchScreen
